import http from '@/api/http'
import { clientBalancesPageLimit } from '@/config/api'
import { majorToMinor } from '@/utils/majorToMinor'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface ClientBalance {
  // One monthly balance snapshot from GET `/balances/person/{person_id}` (cursor-paginated, DESCENDING by calculation_date).

  id: number
  // Balance record id.

  person_id: string
  // Person this balance applies to.

  amount: number
  // Balance amount in major currency units (see backend contract).

  calculation_date: string
  // ISO 8601 datetime (typically midnight-UTC, first of the month) when the snapshot was computed.
}

/**
 * Major-to-minor units conversion for `MoneyCell`. Returns `undefined` when
 * the amount is missing or non-finite so the cell can show its placeholder.
 */
export function clientBalanceAmountMinorUnits(
  balance: Pick<ClientBalance, 'amount'>,
): number | undefined {
  return majorToMinor(balance.amount)
}

export interface ClientBalancesResponse extends PageMeta {
  // Paginated list payload from GET `/balances/person/{person_id}`.

  items: ClientBalance[]
  // Page of monthly balance snapshots for one person.
}

/**
 * Fetches a page of monthly balance snapshots for a single person. Same
 * cursor/limit contract as the other paginated list fetchers.
 *
 * @param personId - Person id (matches the row id in the clients list).
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchClientBalances(
  personId: string,
  cursor?: number,
): Promise<ClientBalancesResponse> {
  const { data } = await http.get<ClientBalancesResponse>(`/balances/person/${personId}`, {
    params: cursorParams(clientBalancesPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log(`[fetchClientBalances] GET /balances/person/${personId} response`, data)
  }
  return data
}
